import React, { useState, useEffect } from 'react';
import { Spin, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import dayjs from 'dayjs';
import { WechatIcon, AlipayIcon } from '../components/PaymentIcons';
import PageSkeleton from '../components/Skeleton';

const GOLD = '#B8892A';
const BD = '#E8E0D0';
const BG = '#F5F2ED';
const MUTED = '#685840';
const MUTED_LIGHT = '#A89070';

const CHECKOUT_STEPS_KEYS = ['mandates.title', 'bidResult.won', 'payment.title', 'inspection.title', 'orders.statusCompleted'];

const PAY_METHODS_KEYS = [
  { key: 'wechat', icon: null, IconComp: WechatIcon, nameKey: 'deposit.wechat', desc: '扫码支付，实时到账', badge: '推荐' },
  { key: 'alipay', icon: null, IconComp: AlipayIcon, nameKey: 'deposit.alipay', desc: '扫码支付，实时到账', badge: null },
  { key: 'deposit', icon: '💴', nameKey: 'deposit.title', desc: '使用账户余额直接抵扣', badge: null },
];

function formatJPY(val) {
  if (val == null) return '—';
  return '¥' + Number(val).toLocaleString('ja-JP') + ' JPY';
}

function formatCNY(val) {
  if (val == null) return '—';
  return '¥' + Number(val).toFixed(2);
}

export default function Payment() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const CHECKOUT_STEPS = CHECKOUT_STEPS_KEYS.map(k => ({ label: t(k) }));
  const PAY_METHODS = PAY_METHODS_KEYS.map(pm => ({ ...pm, name: t(pm.nameKey) }));
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payMethod, setPayMethod] = useState('wechat');
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/orders/${id}`);
        setOrder(data);
      } catch (err) {
        message.error(t('common.failed'));
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const handlePay = async () => {
    setPaying(true);
    try {
      await api.post(`/orders/${id}/pay`, { method: payMethod });
      message.success(t('payment.paySuccess'));
      navigate('/mandates');
    } catch (err) {
      message.error(err.response?.data?.message || t('payment.payFailed'));
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return <PageSkeleton />;
  }

  const o = order || {};
  const feeItems = [
    { k: t('payment.orderAmount'), v: formatJPY(o.lot_price_jpy), vc: undefined },
    { k: t('common.yuan'), v: o.buyer_premium_jpy ? formatJPY(o.buyer_premium_jpy) : '—', vc: undefined },
    { k: t('payment.payMethod'), v: o.service_fee_cny ? formatCNY(o.service_fee_cny) : '—', vc: undefined },
    { k: t('orders.logistics'), v: o.shipping_fee_cny ? formatCNY(o.shipping_fee_cny) : '—', vc: MUTED_LIGHT },
  ];

  const totalCNY = o.total_cny;
  const depositDeduction = o.deposit_deduction_cny;
  const remaining = totalCNY != null && depositDeduction != null ? totalCNY - depositDeduction : totalCNY;

  return (
    <div style={{ background: BG, minHeight: '100vh' }}>
      {/* Progress bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid ' + BD, padding: '12px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center' }}>
          {CHECKOUT_STEPS.map((step, i) => (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: i < CHECKOUT_STEPS.length - 1 ? 1 : 0 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--mono)', fontSize: 11,
                  background: i === 0 ? '#2A6038' : i === 1 ? GOLD : '#EDE8DF',
                  border: '1.5px solid ' + (i === 0 ? '#2A6038' : i === 1 ? GOLD : BD),
                  color: i <= 1 ? '#fff' : MUTED_LIGHT,
                }}>
                  {i === 0 ? '✓' : i + 1}
                </div>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: i === 1 ? GOLD : i === 0 ? '#2A6038' : MUTED_LIGHT, letterSpacing: 1 }}>{step.label}</span>
              </div>
              {i < CHECKOUT_STEPS.length - 1 && (
                <div style={{ flex: 1, height: 1, background: i === 0 ? '#2A6038' : BD, margin: '0 8px' }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Timeout warning bar */}
      <div style={{
        maxWidth: 900, margin: '12px auto 0', padding: '10px 24px',
        background: 'rgba(155,35,53,0.06)', border: '1px solid rgba(155,35,53,0.15)', borderRadius: 4,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>⏰</span>
          <span style={{ fontSize: 12, color: '#9B2335' }}>请在 <strong>72 小时</strong> 内完成付款，逾期订单将自动取消</span>
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 600, color: '#9B2335' }}>23:47:12</div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 24px 60px', display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>
        {/* Left: bill + payment method */}
        <div>
          {/* Bill card */}
          <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 4, overflow: 'hidden', marginBottom: 20, boxShadow: '0 2px 14px rgba(24,20,12,0.08)' }}>
            {/* Card head - dark */}
            <div style={{ padding: '14px 20px', background: '#18140C', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 15, color: '#fff', letterSpacing: 3 }}>{t('payment.title')}</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: 2 }}>
                {o.order_no || id}
              </div>
            </div>

            {/* Lot info */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + BD, display: 'flex', gap: 14, alignItems: 'center' }}>
              <div style={{ fontSize: 36, flexShrink: 0 }}>{o.lot_emoji || '📦'}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: 2, color: MUTED_LIGHT, marginBottom: 4 }}>{o.lot_category?.toUpperCase() || 'LOT'}</div>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 15, color: '#18140C', fontWeight: 500, marginBottom: 4 }}>
                  {o.lot_title || '—'}
                </div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: MUTED_LIGHT }}>
                  {o.house_name && <span>{o.house_name} · </span>}
                  {o.created_at ? dayjs(o.created_at).format('YYYY-MM-DD') : ''}
                </div>
              </div>
              {o.inspection_grade && (
                <div style={{ padding: '4px 10px', borderRadius: 2, background: 'rgba(184,137,42,0.1)', border: '1px solid rgba(184,137,42,0.2)', fontFamily: 'var(--mono)', fontSize: 9, color: GOLD, letterSpacing: 1, flexShrink: 0 }}>
                  {o.inspection_grade} 级
                </div>
              )}
            </div>

            {/* Fee lines */}
            <div style={{ padding: '16px 20px' }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '2px', color: MUTED_LIGHT, textTransform: 'uppercase', marginBottom: 12 }}>费用明细</div>
              {feeItems.map((item) => (
                <div key={item.k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '7px 0', borderBottom: '1px solid #EEEADF', fontSize: 13 }}>
                  <span style={{ color: MUTED }}>{item.k}</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 13, color: item.vc || '#2E2416' }}>{item.v}</span>
                </div>
              ))}

              {/* Total row - dark */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '12px 16px', margin: '8px -20px -16px', background: 'linear-gradient(135deg, #18140C, #2E2416)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 15, color: 'rgba(255,255,255,0.7)', letterSpacing: 2 }}>{t('payment.orderAmount')}</div>
                <div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 24, color: '#D4A843', fontWeight: 500 }}>{formatCNY(totalCNY)}</div>
                  {o.lot_price_jpy && <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'rgba(255,255,255,0.4)', textAlign: 'right', marginTop: 2 }}>成交价 {formatJPY(o.lot_price_jpy)}</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Deposit deduction */}
          {depositDeduction != null && (
            <div style={{ padding: '14px 20px', background: '#EDE8DF', border: '1px solid ' + BD, borderRadius: 4, marginBottom: 20 }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '2px', color: MUTED_LIGHT, marginBottom: 10 }}>保证金抵扣</div>
              {[
                { k: '应付总额', v: formatCNY(totalCNY) },
                { k: '保证金抵扣', v: '-' + formatCNY(depositDeduction), vc: '#2A6038' },
              ].map(({ k, v, vc }) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, fontSize: 13 }}>
                  <span style={{ color: MUTED }}>{k}</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 13, color: vc || '#2E2416' }}>{v}</span>
                </div>
              ))}
              <div style={{ height: 6, background: '#D8D0C5', borderRadius: 3, marginTop: 10, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 3, background: GOLD, width: '100%', transition: 'width 0.8s ease' }} />
              </div>
            </div>
          )}

          {/* Payment methods */}
          <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 4, overflow: 'hidden', marginBottom: 20, boxShadow: '0 2px 14px rgba(24,20,12,0.08)' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BD, fontFamily: 'var(--serif)', fontSize: 15, color: '#18140C', letterSpacing: 2 }}>{t('payment.payMethod')}</div>
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {PAY_METHODS.map((pm) => (
                <div
                  key={pm.key}
                  onClick={() => setPayMethod(pm.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '12px 14px', borderRadius: 3, cursor: 'pointer', transition: 'all 0.16s',
                    border: '1.5px solid ' + (payMethod === pm.key ? GOLD : BD),
                    background: payMethod === pm.key ? 'rgba(184,137,42,0.06)' : '#fff',
                    boxShadow: payMethod === pm.key ? '0 0 0 2px rgba(184,137,42,0.1)' : 'none',
                  }}
                >
                  <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid ' + (payMethod === pm.key ? GOLD : BD), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: payMethod === pm.key ? GOLD : 'transparent' }}>
                    {payMethod === pm.key && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                  <div style={{ fontSize: 22, flexShrink: 0 }}>{pm.IconComp ? <pm.IconComp size={22} /> : pm.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, color: '#2E2416', fontWeight: 500 }}>{pm.name}</div>
                    <div style={{ fontSize: 11, color: MUTED_LIGHT, marginTop: 2 }}>{pm.desc}</div>
                  </div>
                  {pm.badge && (
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 8, padding: '2px 6px', borderRadius: 2, background: 'rgba(42,96,56,0.08)', border: '1px solid rgba(42,96,56,0.15)', color: '#2A6038', letterSpacing: 1, flexShrink: 0 }}>{pm.badge}</div>
                  )}
                </div>
              ))}
            </div>

            {/* QR section for wechat/alipay */}
            {(payMethod === 'wechat' || payMethod === 'alipay') && (
              <div style={{ padding: 20, textAlign: 'center', borderTop: '1px solid ' + BD, background: '#EDE8DF' }}>
                <div style={{ width: 140, height: 140, background: '#fff', border: '1px solid ' + BD, margin: '0 auto 12px', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {payMethod === 'wechat' ? <WechatIcon size={64} /> : <AlipayIcon size={64} />}
                </div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: MUTED, letterSpacing: 1 }}>
                  {payMethod === 'wechat' ? '微信扫码支付' : '支付宝扫码支付'}
                </div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 18, color: GOLD, marginTop: 4 }}>{formatCNY(remaining ?? totalCNY)}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: '#9B2335', marginTop: 6 }}>二维码有效期 04:59</div>
              </div>
            )}
          </div>

          {/* Delivery address card */}
          <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 4, overflow: 'hidden', marginBottom: 20, boxShadow: '0 2px 14px rgba(24,20,12,.08)' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BD, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 15, color: '#18140C', letterSpacing: 2 }}>{t('orders.shippingAddress')}</div>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: GOLD, cursor: 'pointer', letterSpacing: 1 }}>修改 →</span>
            </div>
            <div style={{ padding: '16px 20px', fontSize: 13, lineHeight: 1.9 }}>
              <div style={{ fontWeight: 500, color: '#18140C', marginBottom: 2 }}>{o.customer_name || o.recipient_name || '—'}</div>
              <div style={{ color: MUTED }}>{o.customer_phone || o.recipient_phone || '—'}</div>
              <div style={{ color: MUTED }}>{o.shipping_address || '请在个人中心设置收货地址'}</div>
            </div>
          </div>
        </div>

        {/* Right: summary panel */}
        <div style={{ position: 'sticky', top: 80 }}>
          <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 28px rgba(24,20,12,0.1)', marginBottom: 16 }}>
            <div style={{ padding: '12px 16px', background: '#EDE8DF', borderBottom: '1px solid ' + BD, fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 2, color: MUTED_LIGHT }}>订单摘要</div>
            <div style={{ padding: 16 }}>
              {[
                { k: '订单号', v: o.order_no || id },
                { k: '委托编号', v: o.mandate_no || '—' },
                { k: '成交价格', v: formatJPY(o.lot_price_jpy), gold: true },
                { k: '创建时间', v: o.created_at ? dayjs(o.created_at).format('YYYY-MM-DD HH:mm') : '—' },
              ].map(({ k, v, gold }) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 12 }}>
                  <span style={{ color: MUTED_LIGHT }}>{k}</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: gold ? GOLD : '#2E2416' }}>{v}</span>
                </div>
              ))}
            </div>
            {/* Total row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 16px', background: '#18140C', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ fontFamily: 'var(--serif)', fontSize: 14, color: 'rgba(255,255,255,0.6)', letterSpacing: 1 }}>实付金额</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 20, color: '#D4A843', fontWeight: 500 }}>{formatCNY(remaining ?? totalCNY)}</span>
            </div>
          </div>

          {/* Pay button */}
          <button
            onClick={handlePay}
            disabled={paying}
            style={{
              width: '100%', padding: 14, background: paying ? '#ccc' : GOLD,
              border: 'none', borderRadius: 3, cursor: paying ? 'not-allowed' : 'pointer',
              color: '#fff', fontFamily: 'var(--serif)', fontSize: 17, fontWeight: 600,
              letterSpacing: 2, boxShadow: '0 3px 16px rgba(184,137,42,0.3)', transition: 'all 0.22s',
            }}
            onMouseEnter={(e) => { if (!paying) { e.currentTarget.style.background = '#A07A22'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
            onMouseLeave={(e) => { if (!paying) { e.currentTarget.style.background = GOLD; e.currentTarget.style.transform = 'none'; } }}
          >
            {paying ? t('common.loading') : t('payment.payNow')}
          </button>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: MUTED_LIGHT, textAlign: 'center', marginTop: 10, lineHeight: 1.7 }}>
            支付即视为同意《云川中古服务协议》<br />
            如有疑问，请联系客服
          </div>
        </div>
      </div>
    </div>
  );
}
